import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import { dadosImpressao } from "../../../../../shared/util/dadosImpressao";

function RelatorioMapaVenda({
  dataFiltro,
  dadosClinica,
  auth,
  dadosMapa,
  acao,
  atualizacoes,
  unidadeSelecionada = "",
  unidades = [],
}) {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();

  // Sufixo a acrescentar aos títulos, para não haver dúvidas sobre a que
  // unidade pertence o mapa impresso. Vazio quando é consolidado.
  const unidade = unidades.find((u) => u._id === unidadeSelecionada);
  const sufixoUnidade = unidade ? ` — ${unidade.codigo} (${unidade.nome})` : "";
  // Filtrado por unidade: o cabeçalho mostra a morada dessa unidade.
  const clinicaImpressao = dadosImpressao(dadosClinica, unidade);

  // Este useEffect executa quando o componente é montado ou quando a ação muda
  useEffect(() => {
    // Se uma ação foi especificada (gerar ou exportar), execute-a
    if (acao) {
      if (acao === "gerar") {
        gerarRelatorio();
      } else if (acao === "exportar") {
        exportarExcel();
      }
    }
  }, [acao, atualizacoes]); // Importante: só reexecuta quando a ação muda

  const normalizarNomes = (nome) => {
    if (!nome) return "";
    // normalizar os nomes
    const words = nome.toLowerCase().split(" ");
    // Uppercase the first letter of each word
    const capitalizedWords = words.map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1),
    );
    return capitalizedWords.join(" ");
  };

  const gerarRelatorio = async () => {
    try {
      startLoading();
      const formData = new FormData();
      formData.append(
        "dataInicial",
        dataFiltro.dataInicial.format("YYYY-MM-DD"),
      );
      formData.append("dataFinal", dataFiltro.dataFinal.format("YYYY-MM-DD"));
      // Vazio = consolidado (todas as unidades).
      if (unidadeSelecionada)
        formData.append("estabelecimento", unidadeSelecionada);
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/entradas/mapavenda/${dadosClinica._id}`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      if (responseData.data.length === 0) {
        toast.error("Sem dados para gerar o mapa!");
      } else {
        // Agrupar documentos por tipo
        const documentosAgrupados = agruparPorTipo(responseData.data);

        //Enviar dados de entrada para PDF
        dadosMapa(
          documentosAgrupados,
          clinicaImpressao,
          moment(dataFiltro.dataInicial).format("DD-MM-YYYY"),
          moment(dataFiltro.dataFinal).format("DD-MM-YYYY"),
        );
      }
    } catch (err) {
      console.error("err", err);
      toast.error("Erro ao gerar relatório");
    } finally {
      stopLoading();
    }
  };

  const exportarExcel = async () => {
    try {
      startLoading();
      const formData = new FormData();
      formData.append(
        "dataInicial",
        dataFiltro.dataInicial.format("YYYY-MM-DD"),
      );
      formData.append("dataFinal", dataFiltro.dataFinal.format("YYYY-MM-DD"));
      // Vazio = consolidado (todas as unidades).
      if (unidadeSelecionada)
        formData.append("estabelecimento", unidadeSelecionada);

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/entradas/mapavenda/${dadosClinica._id}`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      if (responseData.data.length === 0) {
        toast.error("Sem dados para exportar!");
        return;
      }

      // Agrupar documentos por tipo
      const documentosAgrupados = agruparPorTipo(responseData.data);

      // Calcular o total geral
      const total = responseData.data.reduce(
        (accumulator, currentValue) => accumulator + currentValue.valor,
        0,
      );

      // Criar o workbook
      const wb = XLSX.utils.book_new();

      // Preparar os dados para o Excel
      const excelData = [];

      // Adicionar cabeçalho da clínica (sem imagem) - APENAS UMA VEZ
      excelData.push([normalizarNomes(clinicaImpressao.nome)]);
      excelData.push([`Telef: ${clinicaImpressao.contacto}`]);
      excelData.push([clinicaImpressao.endereco]);
      excelData.push([`NIF: ${clinicaImpressao.nif}`]);
      excelData.push([]); // Linha vazia

      // Adicionar título do mapa
      const dataInicialFormatada = moment(dataFiltro.dataInicial).format(
        "DD-MM-YYYY",
      );
      const dataFinalFormatada = moment(dataFiltro.dataFinal).format(
        "DD-MM-YYYY",
      );
      excelData.push([
        `Mapa Movimentos Mensal, de ${dataInicialFormatada} a ${dataFinalFormatada}${sufixoUnidade}`,
      ]);
      excelData.push([]); // Linha vazia

      // Adicionar cabeçalhos da tabela
      excelData.push([
        "Origem",
        "NIF Entidade",
        "Designação Entidade",
        "Série",
        "Tipo Doc",
        "Nr Doc",
        "Data",
        "Valor",
      ]);

      // Adicionar dados agrupados por tipo
      documentosAgrupados.forEach((grupo, indexGrupo) => {
        // Linha vazia antes do grupo (exceto o primeiro)
        if (indexGrupo > 0) {
          excelData.push([]);
        }

        // Adicionar documentos do grupo
        grupo.documentos.forEach((dt) => {
          excelData.push([
            "CV",
            dt.nifEntidade,
            normalizarNomes(dt.entidade),
            dt.serie,
            dt.tipoDocumento,
            dt.nrDocumento,
            dt.data,
            dt.valor,
          ]);
        });

        // Adicionar linha de subtotal
        excelData.push([
          "",
          "",
          "",
          "",
          "",
          "",
          `Total ${grupo.tipo}`,
          grupo.subtotal,
        ]);

        // Linha vazia depois do grupo
        excelData.push([]);
      });

      // Adicionar linhas vazias
      excelData.push([]);
      excelData.push([]);

      // Adicionar quadro resumo
      excelData.push([
        `Quadro Resumo IVA: ${dataInicialFormatada} a ${dataFinalFormatada}${sufixoUnidade}`,
      ]);
      excelData.push(["Valor total", total]);

      // Criar worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Aplicar estilos
      const range = XLSX.utils.decode_range(ws["!ref"]);

      // Estilo para o cabeçalho da clínica (primeiras 4 linhas)
      for (let row = 0; row <= 3; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true, sz: 12 },
            alignment: { horizontal: "left" },
          };
        }
      }

      // Estilo para o título do mapa (linha 5)
      const tituloCell = XLSX.utils.encode_cell({ r: 5, c: 0 });
      if (ws[tituloCell]) {
        ws[tituloCell].s = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center" },
        };
      }

      // Mesclar células do título
      ws["!merges"] = ws["!merges"] || [];
      ws["!merges"].push({ s: { r: 5, c: 0 }, e: { r: 5, c: 7 } });

      // Estilo para cabeçalhos da tabela (linha 7)
      for (let col = 0; col <= 7; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 7, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true, sz: 11 },
            fill: { fgColor: { rgb: "E0E0E0" } },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
            alignment: { horizontal: col === 7 ? "right" : "left" },
          };
        }
      }

      // Formatar valores numéricos e aplicar bordas nas células de dados
      const dataStartRow = 8;
      const dataEndRow = dataStartRow + responseData.data.length - 1;

      for (let row = dataStartRow; row <= dataEndRow; row++) {
        for (let col = 0; col <= 7; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (ws[cellAddress]) {
            // Aplicar bordas
            ws[cellAddress].s = ws[cellAddress].s || {};
            ws[cellAddress].s.border = {
              top: { style: "thin", color: { rgb: "D0D0D0" } },
              bottom: { style: "thin", color: { rgb: "D0D0D0" } },
              left: { style: "thin", color: { rgb: "D0D0D0" } },
              right: { style: "thin", color: { rgb: "D0D0D0" } },
            };

            // Alinhar valores à direita na coluna de valor
            if (col === 7) {
              ws[cellAddress].s.alignment = { horizontal: "right" };
              ws[cellAddress].s.numFmt = "#,##0.00";
            }
          }
        }
      }

      // Estilo para o quadro resumo
      const resumoTituloRow = dataEndRow + 3;
      const resumoValorRow = dataEndRow + 4;

      const resumoTituloCell = XLSX.utils.encode_cell({
        r: resumoTituloRow,
        c: 0,
      });
      if (ws[resumoTituloCell]) {
        ws[resumoTituloCell].s = {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: "left" },
        };
      }

      // Mesclar células do título do resumo
      ws["!merges"].push({
        s: { r: resumoTituloRow, c: 0 },
        e: { r: resumoTituloRow, c: 7 },
      });

      // Estilo para o valor total
      const valorTotalLabelCell = XLSX.utils.encode_cell({
        r: resumoValorRow,
        c: 0,
      });
      const valorTotalCell = XLSX.utils.encode_cell({
        r: resumoValorRow,
        c: 1,
      });

      if (ws[valorTotalLabelCell]) {
        ws[valorTotalLabelCell].s = {
          font: { bold: true, sz: 11 },
          alignment: { horizontal: "left" },
        };
      }

      if (ws[valorTotalCell]) {
        ws[valorTotalCell].s = {
          font: { bold: true, sz: 11 },
          alignment: { horizontal: "right" },
          numFmt: "#,##0.00",
        };
      }

      // Definir largura das colunas
      ws["!cols"] = [
        { wch: 10 }, // Origem
        { wch: 15 }, // NIF Entidade
        { wch: 30 }, // Designação Entidade
        { wch: 10 }, // Série
        { wch: 12 }, // Tipo Doc
        { wch: 12 }, // Nr Doc
        { wch: 12 }, // Data
        { wch: 15 }, // Valor
      ];

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Mapa de Vendas");

      // Gerar o arquivo e fazer download
      const fileName = `MapaVendas${unidade ? "_" + unidade.codigo : ""}_${dataInicialFormatada}_${dataFinalFormatada}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success("Excel exportado com sucesso!");
    } catch (err) {
      console.error("err", err);
      toast.error("Erro ao exportar Excel");
    } finally {
      stopLoading();
    }
  };

  const agruparPorTipo = (documentos) => {
    /* Sem `Event`: não é um documento de venda, é o evento de cancelamento
       enviado à e-fatura. Estava aqui e o mapa desenhava-lhe uma secção com
       subtotal, como se cancelar fosse vender. O servidor já não os manda —
       isto é a segunda tranca, para nenhum outro consumidor os agrupar. */
    const ordemTipos = ["FRE", "TVE", "FTE", "NCE"];

    // Agrupar documentos por tipo
    const grupos = documentos.reduce((acc, doc) => {
      const tipo = doc.tipoDocumento;
      if (!acc[tipo]) {
        acc[tipo] = [];
      }
      acc[tipo].push(doc);
      return acc;
    }, {});

    // Ordenar pelos tipos definidos e calcular subtotais
    const documentosAgrupados = [];
    ordemTipos.forEach((tipo) => {
      if (grupos[tipo] && grupos[tipo].length > 0) {
        // const subtotal = grupos[tipo].reduce((sum, doc) => sum + doc.valor, 0);
        // documentosAgrupados.push({
        //   tipo,
        //   documentos: grupos[tipo],
        //   subtotal,
        // });

        const documentosOrdenados = grupos[tipo].sort(
          (a, b) => Number(a.nrDocumento) - Number(b.nrDocumento),
        );
        const subtotal = documentosOrdenados.reduce(
          (sum, doc) => sum + doc.valor,
          0,
        );
        documentosAgrupados.push({
          tipo,
          documentos: documentosOrdenados,
          subtotal,
        });
      }
    });

    return documentosAgrupados;
  };

  return <div></div>;
}

export default RelatorioMapaVenda;
